'use client';

import React, { Fragment, isValidElement } from 'react';

import '@bytel/product-wall/style';
import { Icon, IconColor, IconName, IconSize, Text, TextLevels } from '@bytel/trilogy-react';

import { ComparatorCell, ComparatorRow, ComparatorRowHead, ComparatorRowSubHead } from '@comparator/components';
import { MobileCharacteristic } from '@comparator/helpers';

interface ComparatorLineProps {
    title?: string;
    characteristics: MobileCharacteristic[];
}

export function MobileComparatorLine({ title, characteristics }: Readonly<ComparatorLineProps>) {
    if (!characteristics || characteristics.length === 0) return null;

    if (characteristics.length > 0 && characteristics.every((c) => isValidElement(c))) {
        return (
            <ComparatorRow>
                {characteristics.map((characteristic) => characteristic)}
                {Array(Math.max(0, 3 - characteristics.length))
                    .fill(null)
                    .map((_, index) => (
                        <ComparatorCell className='is-hidden-mobile' key={'placeholder-' + index}>
                            &nbsp;
                        </ComparatorCell>
                    ))}
            </ComparatorRow>
        );
    }

    return (
        <>
            {title && <ComparatorRowHead label={title} />}
            {characteristics.map((characteristic, index) => {
                if (
                    typeof characteristic === 'object' &&
                    'name' in characteristic &&
                    Array.isArray(characteristic.values) &&
                    characteristic.values.some((v) => v !== undefined)
                ) {
                    return (
                        <Fragment key={characteristic.name ?? index}>
                            <ComparatorRowSubHead label={characteristic.name} />
                            <ComparatorRow>
                                {characteristic.values.map((value, vIdx) => {
                                    let valueContent;
                                    if (isValidElement(value)) {
                                        valueContent = value;
                                    } else if (typeof value === 'boolean') {
                                        valueContent = value ? (
                                            <Icon
                                                name={IconName.CHECK}
                                                color={IconColor.SUCCESS}
                                                size={IconSize.SMALL}
                                                className='m-auto'
                                            />
                                        ) : (
                                            <Icon
                                                name={IconName.TIMES}
                                                color={IconColor.GREY}
                                                size={IconSize.SMALL}
                                                className='m-auto'
                                            />
                                        );
                                    } else if (typeof value === 'string') {
                                        valueContent = <Text level={TextLevels.TWO}>{value}</Text>;
                                    }
                                    return (
                                        <ComparatorCell
                                            key={`${characteristic.name}-${vIdx}`}
                                            className='is-flex is-aligned-center'
                                        >
                                            {valueContent}
                                        </ComparatorCell>
                                    );
                                })}
                                {Array(Math.max(0, 3 - characteristic.values.length))
                                    .fill(null)
                                    .map((_, index, array) => (
                                        <ComparatorCell
                                            className={index === array.length - 1 ? 'is-hidden-mobile' : ''}
                                            key={'empty-' + index}
                                        >
                                            &nbsp;
                                        </ComparatorCell>
                                    ))}
                            </ComparatorRow>
                        </Fragment>
                    );
                }
            })}
        </>
    );
}
